/**
 * useCrmOverviewFilters - Hook para gerenciar filtros do dashboard Visão Geral
 * 
 * Gerencia estado de filtros de período, workspace e vendedor.
 * Persiste a última seleção em localStorage.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths
} from 'date-fns';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const filtersLogger = createLogger('CrmOverviewFilters');

/**
 * Tipos de período predefinidos
 */
export type PeriodPreset = 
  | 'today' 
  | 'yesterday' 
  | '7days' 
  | '15days' 
  | 'month' 
  | 'lastMonth' 
  | 'custom';

/**
 * Interface para período selecionado
 */
export interface PeriodFilter {
  preset: PeriodPreset;
  startDate: Date;
  endDate: Date;
}

/**
 * Interface para filtros do dashboard
 */
export interface CrmOverviewFilters {
  period: PeriodFilter;
  workspaceId: string | null;
  sellerId: string | null;
}

/**
 * Chave para persistência em localStorage
 */
const STORAGE_KEY = 'crm_overview_filters';

/**
 * Calcula datas baseado no preset
 */
export function calculatePeriodDates(preset: PeriodPreset): { startDate: Date; endDate: Date } {
  const today = new Date();
  
  switch (preset) {
    case 'today':
      return {
        startDate: startOfDay(today),
        endDate: endOfDay(today)
      };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return {
        startDate: startOfDay(yesterday),
        endDate: endOfDay(yesterday)
      };
    case '7days':
      return {
        startDate: startOfDay(subDays(today, 6)),
        endDate: endOfDay(today)
      };
    case '15days':
      return {
        startDate: startOfDay(subDays(today, 14)),
        endDate: endOfDay(today)
      };
    case 'month':
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today)
      };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth)
      };
    case 'custom':
    default:
      // Para custom, retorna mês atual como padrão
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today)
      };
  }
}

/**
 * Gera filtros padrão (mês atual, sem filtros de workspace/vendedor)
 */
function getDefaultFilters(): CrmOverviewFilters {
  const { startDate, endDate } = calculatePeriodDates('month');
  return {
    period: {
      preset: 'month',
      startDate,
      endDate
    },
    workspaceId: null,
    sellerId: null
  };
}

/**
 * Carrega filtros do localStorage
 */
function loadFiltersFromStorage(): CrmOverviewFilters | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    
    // Recalcular datas se não for custom (para evitar datas antigas)
    if (parsed.period?.preset && parsed.period.preset !== 'custom') {
      const { startDate, endDate } = calculatePeriodDates(parsed.period.preset);
      parsed.period.startDate = startDate;
      parsed.period.endDate = endDate;
    } else {
      // Converter strings para Date
      parsed.period.startDate = new Date(parsed.period.startDate);
      parsed.period.endDate = new Date(parsed.period.endDate);
    }
    
    return parsed;
  } catch (e) {
    filtersLogger.warn('Erro ao carregar filtros do storage', e);
    return null;
  }
}

/**
 * Salva filtros no localStorage
 */
function saveFiltersToStorage(filters: CrmOverviewFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      period: {
        preset: filters.period.preset,
        startDate: filters.period.startDate.toISOString(),
        endDate: filters.period.endDate.toISOString()
      },
      workspaceId: filters.workspaceId,
      sellerId: filters.sellerId
    }));
  } catch (e) {
    filtersLogger.warn('Erro ao salvar filtros no storage', e);
  }
}

/**
 * Hook para gerenciar filtros do dashboard Visão Geral
 */
export function useCrmOverviewFilters() {
  // Estado dos filtros - inicializa com localStorage ou padrão
  const [filters, setFiltersState] = useState<CrmOverviewFilters>(() => {
    const stored = loadFiltersFromStorage();
    return stored || getDefaultFilters();
  });

  // Persiste alterações no localStorage
  useEffect(() => {
    saveFiltersToStorage(filters);
    filtersLogger.debug('Filtros atualizados', {
      preset: filters.period.preset,
      workspaceId: filters.workspaceId,
      sellerId: filters.sellerId
    });
  }, [filters]);

  // Atualiza período por preset
  const setPeriodPreset = useCallback((preset: PeriodPreset) => {
    const { startDate, endDate } = calculatePeriodDates(preset);
    setFiltersState(prev => ({
      ...prev,
      period: { preset, startDate, endDate }
    }));
  }, []);

  // Atualiza período com datas customizadas
  const setCustomPeriod = useCallback((startDate: Date, endDate: Date) => {
    setFiltersState(prev => ({
      ...prev,
      period: { preset: 'custom', startDate, endDate }
    }));
  }, []);

  // Atualiza workspace
  const setWorkspaceId = useCallback((workspaceId: string | null) => {
    setFiltersState(prev => ({ ...prev, workspaceId }));
  }, []);

  // Atualiza vendedor
  const setSellerId = useCallback((sellerId: string | null) => {
    setFiltersState(prev => ({ ...prev, sellerId }));
  }, []);

  // Limpa todos os filtros (volta ao padrão)
  const clearFilters = useCallback(() => {
    setFiltersState(getDefaultFilters());
  }, []);

  // Verifica se há filtros ativos (diferentes do padrão)
  const hasActiveFilters = useMemo(() => {
    return (
      filters.period.preset !== 'month' ||
      filters.workspaceId !== null ||
      filters.sellerId !== null
    );
  }, [filters]);

  // Retorno do hook
  return {
    // Estado dos filtros
    filters,
    
    // Setters individuais
    setPeriodPreset,
    setCustomPeriod,
    setWorkspaceId,
    setSellerId,
    
    // Ações
    clearFilters,
    
    // Estado derivado
    hasActiveFilters
  };
}
